import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Modal,
  ModalTitle,
  ModalContent,
  ModalActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  InputAdornment,
  IconButton,
} from "@deere/fuel-react";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

// Reusable country picker: search, select all/none, pre-checked values, "Add" hands the chosen list back to the caller
const ModalSelectCountry = ({ show, handleClose, countryList = [], selectedCountries = [], onAdd, title = "Select Countries" }) => {
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [checkedCountries, setCheckedCountries] = useState([]);
  const searchInputRef = useRef(null);

  const resetSearch = useCallback(() => {
    setSearchText("");
    setDebouncedSearchText("");
  }, []);

  useEffect(() => {
    if (show) {
      setCheckedCountries(selectedCountries);
      resetSearch();
    }
  }, [show, selectedCountries, resetSearch]);

  // Keep typing responsive: the list only re-filters once the user pauses
  useEffect(() => {
    const timerId = setTimeout(() => setDebouncedSearchText(searchText), 300);
    return () => clearTimeout(timerId);
  }, [searchText]);

  const filteredCountries = useMemo(
    () => countryList.filter((country) => country.toLowerCase().includes(debouncedSearchText.toLowerCase())),
    [countryList, debouncedSearchText]
  );

  const isAllSelected = countryList.length > 0 && checkedCountries.length === countryList.length;

  const toggleSelectAll = () => {
    setCheckedCountries(isAllSelected ? [] : [...countryList]);
  };

  const toggleCountry = (countryName) => {
    setCheckedCountries((prevState) =>
      prevState.includes(countryName) ? prevState.filter((name) => name !== countryName) : [...prevState, countryName]
    );
  };

  const clearSearch = () => {
    resetSearch();
    searchInputRef.current?.focus();
  };

  const closeModal = () => {
    resetSearch();
    handleClose();
  };

  const addHandler = () => {
    onAdd(checkedCountries);
    closeModal();
  };

  return (
    <Modal open={show} onClose={closeModal} fullWidth>
      <ModalTitle>{title}</ModalTitle>
      <ModalContent>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <TextField
              placeholder="Search country..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              inputRef={searchInputRef}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              endAdornment={
                searchText ? (
                  <InputAdornment position="end">
                    <IconButton aria-label="Clear search" size="small" onClick={clearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
              fullWidth
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={checkedCountries.length > 0 && !isAllSelected}
                  onChange={toggleSelectAll}
                />
              }
              label={isAllSelected ? "Deselect All" : "Select All"}
            />
          </Grid>
          <Grid item container direction="column" sx={{ height: 300, overflowY: "auto" }}>
            {filteredCountries.length === 0 && <Typography sx={{ p: 1 }}>No countries found</Typography>}
            {filteredCountries.map((country) => (
              <Grid item key={country}>
                <FormControlLabel
                  control={<Checkbox checked={checkedCountries.includes(country)} onChange={() => toggleCountry(country)} />}
                  label={country}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={closeModal}>
          Cancel
        </Button>
        <Button variant="primary" onClick={addHandler} disabled={checkedCountries.length === 0}>
          Add
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default ModalSelectCountry;
